/**
 * dvSymbolZ.ts
 * 
 * 功能说明：
 * 用于动态设置线图数据点的 Z 轴层级（z-index）。
 * 
 * 核心功能：
 * 1. 通过 dvSymbolZ 配置项，允许用户为线图的数据点（symbol）设置 Z 轴层级
 * 2. 在每次系列更新后，自动遍历所有线图，根据 dvSymbolZ 配置调整数据点的渲染层级
 * 3. 支持与原有的 zlevel 配置结合使用，提供更灵活的图层控制
 * 
 * 使用场景：
 * - 当需要将某些重要的数据点显示在更高层级时
 * - 处理数据点重叠时的显示优先级问题
 * - 创建特殊的视觉效果，如突出显示特定数据点
 * 
 * 配置方式：
 * series-line.dvSymbolZ: number
 */
import { EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import GlobalModel from 'echarts/lib/model/Global';
import { type default as SymbolClz } from 'echarts/lib/chart/helper/Symbol';
import { DvLineSeriesModel } from './type';
import { isValidNumericValue } from './util';

/**
 * 解析并应用 dvSymbolZ 配置
 * 
 * 工作流程：
 * 1. 获取所有线图类型的系列模型
 * 2. 遍历每个线图系列，检查是否配置了 dvSymbolZ
 * 3. 如果配置了有效的 dvSymbolZ 值，则应用到该系列的所有数据点上
 * 4. 使用 setZ() 方法设置每个数据点的 Z 轴层级
 * 
 * @param ecModel ECharts 全局模型
 */
function parseDvSymbolZ(ecModel: GlobalModel) {
  // 获取所有线图类型的系列
  const lineModels = ecModel.getSeriesByType('line');
  
  // 遍历每个线图系列
  lineModels.forEach((model: DvLineSeriesModel) => {
    const data = model.getData();
    const dvSymbolZ = model.get('dvSymbolZ');
    const zLevel = model.get('zlevel');
    
    // 如果没有配置 dvSymbolZ 或不是有效数值，则跳过
    if (!isValidNumericValue(dvSymbolZ)) {
      return;
    }

    // 遍历该系列的所有数据点图形元素
    data.eachItemGraphicEl((el: SymbolClz) => {
      if (!el) {
        return;
      }
      // 设置数据点的 Z 轴层级
      el.setZ(dvSymbolZ, zLevel);
    });
  });
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('series:afterupdate', ecModel => {
    parseDvSymbolZ(ecModel);
  });
}
